/**
 * Compare a left-hand value (resolved from the current selection of the
 * referenced Characteristic) against the right-hand value declared by the
 * rule. Mirrors the legacy querybuilder operators.
 *
 * Unknown operators log a console.warn once and return false — better than
 * crashing the entire rules pass on a future cloud-side operator.
 */

const seenUnknownOperators = new Set<string>();

export function evaluateOperator(
    operator: string,
    left: unknown,
    right: unknown,
): boolean {
    switch (operator) {
        case 'equal':
        case '=':
            return looseEqual(left, right);

        case 'not_equal':
        case '!=':
            return !looseEqual(left, right);

        case 'greater':
        case '>':
            return toNumber(left) > toNumber(right);

        case 'greater_or_equal':
        case '>=':
            return toNumber(left) >= toNumber(right);

        case 'less':
        case '<':
            return toNumber(left) < toNumber(right);

        case 'less_or_equal':
        case '<=':
            return toNumber(left) <= toNumber(right);

        // A multi-value selection (Checkbox, Mehrfachauswahl) matches when ANY
        // picked value is in the list — legacy's valueIn (app/helper.js)
        // checks each value on its own. Comparing the whole array against
        // each entry only ever matched a single pick.
        case 'in':
            return anyIn(left, right);

        case 'not_in':
            return !anyIn(left, right);

        case 'is_null':
        case 'is_empty':
            return isNullish(left);

        case 'is_not_null':
        case 'is_not_empty':
            return !isNullish(left);

        case 'contains':
            return String(left ?? '').includes(String(right ?? ''));

        case 'not_contains':
            return !String(left ?? '').includes(String(right ?? ''));

        case 'begins_with':
            return String(left ?? '').startsWith(String(right ?? ''));

        case 'ends_with':
            return String(left ?? '').endsWith(String(right ?? ''));

        default:
            if (!seenUnknownOperators.has(operator)) {
                seenUnknownOperators.add(operator);
                console.warn(`[Configento] unknown rule operator "${operator}" — treated as false.`);
            }
            return false;
    }
}

/**
 * Loose equality that handles the wire-format quirk that everything is
 * stringified. `"100"` should equal `100`; `"none_21"` only equals itself.
 */
function looseEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    // Number coercion when both look numeric.
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb) && String(na) === String(a) && String(nb) === String(b)) {
        return na === nb;
    }
    return String(a) === String(b);
}

function anyIn(left: unknown, right: unknown): boolean {
    const candidates = toArray(right);
    return toArray(left).some((l) => candidates.some((r) => looseEqual(l, r)));
}

function toNumber(v: unknown): number {
    if (typeof v === 'number') return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
}

function toArray(v: unknown): unknown[] {
    if (Array.isArray(v)) return v;
    if (v == null) return [];
    return [v];
}

function isNullish(v: unknown): boolean {
    if (v == null) return true;
    if (typeof v === 'string' && v.length === 0) return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
}
